import React, { useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import t from 'react-translate';
import { AngularContext } from 'react-app';

import { RootState } from 'redux/schemas';
import { updateLectureComponent } from 'redux/actions/lecture-pages';
import { ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';
import { ActivityProgress, TimelineActivity } from 'redux/schemas/models/activity';
import { AttendeeInfoStatus, TimelineInPersonEvent } from 'redux/schemas/models/in-person-event';
import { useAppDispatch } from 'redux/store';

import { useLectureComponentContext } from 'components/lecture-component-provider';
import { Timezone } from 'live_sessions/components/use-timezones';
import { getFormattedOffset } from 'shared/components/nv-timezone-selector';

export interface InPersonEventSessionUpdate {
  description?: string;
  title?: string;
  startTime?: string;
  endTime?: string;
  timeZone?: string;
  location?: string;
}

export interface InPersonEventUpdate {
  title?: string;
  isTodo?: boolean;
  eventSessions?: InPersonEventSessionUpdate[];
}

export const useGetInPersonEventLectureComponent = (passedLectureComponentId?: number) => {
  const contextLectureComponentId = useLectureComponentContext().lectureComponentId;
  const lectureComponentId = String(passedLectureComponentId ?? contextLectureComponentId);

  return useSelector<RootState>(
    (state: RootState) => state.models.lectureComponents[lectureComponentId] as NLectureComponent<ComponentType.IN_PERSON_EVENT>,
  );
};

export const useUpdateInPersonEvent = () => {
  const dispatch = useAppDispatch();
  const { catalogId, lecturePageId } = useLectureComponentContext();
  const lectureComponent = useGetInPersonEventLectureComponent();

  return useCallback(
    (updates: InPersonEventUpdate | InPersonEventSessionUpdate, key?: string) => {
      let inPersonEvent;

      if (key) {
        inPersonEvent = {
          [key]: [{ ...updates }],
        };
      } else {
        inPersonEvent = updates;
      }

      const componentData = {
        id: lectureComponent.id,
        type: lectureComponent.type,
        index: lectureComponent.index,
        inPersonEvent,
      };

      return dispatch(
        updateLectureComponent({
          catalogId,
          lecturePageId,
          componentData,
        }),
      );
    },
    [dispatch, catalogId, lecturePageId, lectureComponent],
  );
};

export const formatDate = (time: string, timezone?: string): string => (
  timezone
    ? moment.tz(time, timezone).format('ddd, MM/DD/YYYY h:mmA')
    : moment(time).format('ddd, MM/DD/YYYY h:mmA')
);

export const useFormatTimezone = () => {
  const { injectServices } = useContext(AngularContext);
  const [TimezonesService] = injectServices(['TimezonesService']);
  const allTimezones: Timezone[] = TimezonesService.timezones;

  return useCallback((timeZone: string): string => {
    const timezone = allTimezones.find((tz) => tz.longName === timeZone);
    if (!timezone) return '';

    const translatedName = t.TIMEZONE[timezone.translationKey]?.() || timezone.shortName;
    return `${translatedName} - ${getFormattedOffset(timezone)}`;
  }, [allTimezones]);
};
interface InPersonEventActivityTextProps {
  activity: TimelineActivity | TimelineInPersonEvent;
}

export const InPersonEventActivityText: React.FC<InPersonEventActivityTextProps> = ({ activity }) => {
  const formatTimezone = useFormatTimezone();
  const eventSession = (activity as TimelineInPersonEvent)?.eventSessions?.[0];

  const startTime = eventSession?.startTime;
  const endTime = eventSession?.endTime;
  const timezoneString = eventSession?.timeZone;

  const formatTime = (time: string) => {
    if (!time) return '';
    return timezoneString
      ? moment.tz(time, timezoneString).format('M/D/YYYY h:mmA')
      : moment(time).format('M/D/YYYY h:mmA');
  };

  const formattedTimezone = timezoneString ? formatTimezone(timezoneString) : '';

  if (!startTime) {
    return null;
  }

  return (
    <div className='activity-status-text course-title-xxs text-gray-2 d-flex flex-column'>
      <div>
        {formatTime(startTime)}
        {startTime && endTime && (
          <>
            {` ${t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.LOWERCASE_TO()} `}
          </>
        )}
        {formatTime(endTime)}
      </div>
      {formattedTimezone && (
        <div className='align-self-end'>
          {formattedTimezone}
        </div>
      )}
    </div>
  );
};
